import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Hammer, Users, Globe, TrendingUp, Home, Shield } from "lucide-react";

/**
 * Featured editorial article surfaced at the top of the Housing listing.
 * This is a static write-up (not a user-submitted project), so it lives in
 * a hand-crafted page instead of the /housing-projects collection.
 */
const H2 = ({ children }) => (
  <h2 className="font-display text-3xl mt-12 leading-[1.1]">{children}</h2>
);
const H3 = ({ children, icon: Ic }) => (
  <h3 className="font-display text-2xl mt-10 flex items-center gap-3">
    {Ic && <Ic className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />}
    {children}
  </h3>
);
const P = ({ children }) => <p className="mt-4 text-base leading-relaxed">{children}</p>;
const UL = ({ items, twoCol = false }) => (
  <ul className={`mt-4 grid ${twoCol ? "sm:grid-cols-2" : ""} gap-x-8 gap-y-2 text-sm`}>
    {items.map((it) => (
      <li key={it} className="pl-4 relative before:content-['•'] before:absolute before:left-0">{it}</li>
    ))}
  </ul>
);
const Cite = () => (
  <span className="text-[11px] font-mono ml-1" style={{ color: "var(--wofy-text-soft)" }}>
    (<a href="https://www.habitat.org/" target="_blank" rel="noopener noreferrer" className="underline">Habitat for Humanity</a>)
  </span>
);

export default function HabitatForHumanity() {
  return (
    <article data-testid="habitat-page" className="max-w-[820px] mx-auto px-6 lg:px-10 py-16">
      <Link to="/housing" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider mb-8 hover:opacity-80" style={{ color: "var(--wofy-text-soft)" }}>
        <ArrowLeft className="w-3 h-3" /> Back to Housing
      </Link>

      <div className="overline mb-3">FEATURED · GLOBAL HOUSING</div>
      <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">
        Habitat for Humanity: Building Homes, Communities, and Hope
      </h1>

      <div className="mt-8 aspect-[16/8] rounded-2xl overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80"
          alt="A family and volunteers building a new home together"
          className="w-full h-full object-cover"
        />
      </div>

      <P>
        Few nonprofit organizations have had as much impact on affordable housing as Habitat for Humanity. Since its founding in 1976, Habitat has helped millions of people improve their living conditions by partnering with families, volunteers, businesses, governments, and donors to build and renovate safe, affordable homes. Its philosophy is simple but powerful: <b>everyone deserves a decent place to live, and communities become stronger when people work together.</b>
      </P>
      <P>
        Today, Habitat for Humanity is recognized as one of the world's leading housing organizations, operating throughout the United States and in dozens of countries around the globe. Its innovative approach to affordable homeownership has inspired governments, nonprofits, churches, and community organizations worldwide.
      </P>

      <H2>A Vision Born from Faith and Service</H2>
      <P>
        Habitat for Humanity was founded in 1976 by <b>Millard Fuller</b> and <b>Linda Fuller</b>. Their vision developed from experiences at Koinonia Farm, an interracial Christian farming community in Americus, Georgia, where they worked alongside farmer and biblical scholar Clarence Jordan. Together they developed the concept of "partnership housing," where families in need and volunteers work side by side to build homes that are affordable rather than profit-driven.<Cite />
      </P>
      <P>
        The Fullers believed poverty could not be solved simply by giving away houses. Instead, families should become active partners in building their own future. This philosophy continues to define Habitat today.
      </P>

      <H2>The Habitat Philosophy</H2>
      <P>Habitat for Humanity's mission extends far beyond constructing buildings. The organization believes that:</P>
      <UL items={["Safe housing improves health.", "Children perform better in school when they have stable homes.", "Homeownership creates financial stability.", "Strong neighborhoods create stronger communities.", "Everyone deserves dignity regardless of income."]} twoCol />
      <P>
        Habitat describes its vision as <em>"a world where everyone has a decent place to live."</em><Cite />
      </P>

      <H3 icon={Hammer}>How Habitat Works</H3>
      <P>Unlike traditional charities that simply give away homes, Habitat partners with families. The organization typically:</P>
      <UL items={["Acquires land or renovates existing homes.", "Raises donations and building materials.", "Recruits skilled and unskilled volunteers.", "Works alongside future homeowners.", "Helps arrange affordable financing.", "Provides homeowner education."]} twoCol />
      <P>The result is affordable housing built through cooperation rather than charity alone.</P>

      <H3>Sweat Equity</H3>
      <P>One of Habitat's best-known principles is "sweat equity." Future homeowners contribute significant volunteer hours helping build:</P>
      <UL items={["Their own homes", "Other Habitat homes", "Habitat ReStores", "Community projects"]} twoCol />
      <P>The exact number of required hours varies by local Habitat affiliate.</P>
      <P>
        Sweat equity reduces construction costs while helping homeowners gain practical skills and develop a strong sense of ownership. It also fosters relationships between volunteers and partner families.<Cite />
      </P>

      <H3>Who Qualifies?</H3>
      <P>Each Habitat affiliate establishes its own eligibility requirements, but applicants are generally evaluated using three broad criteria:</P>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        {[
          ["1", "Need", "Applicants must demonstrate that their current housing situation is inadequate, unsafe, overcrowded, unhealthy, or financially unsustainable."],
          ["2", "Ability to Pay", "Habitat homes are not free. Families must demonstrate that they can make affordable monthly mortgage payments and meet other financial responsibilities."],
          ["3", "Willingness to Partner", "Applicants agree to complete sweat equity hours, attend homeowner education classes, and actively participate throughout the construction process."],
        ].map(([n, t, d]) => (
          <div key={n} className="wofy-card">
            <div className="font-display text-3xl" style={{ color: "var(--wofy-primary)" }}>{n}</div>
            <div className="font-display text-lg mt-2">{t}</div>
            <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>{d}</p>
          </div>
        ))}
      </div>
      <P>Because demand often exceeds available homes, many qualified applicants may wait months or even years before receiving assistance.</P>

      <H3>Homeowner Education</H3>
      <P>Habitat prepares families for successful homeownership by offering education in areas such as:</P>
      <UL items={["Budgeting", "Financial management", "Home maintenance", "Property insurance", "Mortgage responsibilities", "Energy conservation", "Community involvement"]} twoCol />
      <P>These programs help families maintain long-term housing stability.</P>

      <H2>Funding the Mission</H2>
      <P>Habitat operates through a combination of:</P>
      <UL items={["Individual donations", "Corporate sponsorships", "Foundation grants", "Government partnerships", "Church support", "Community fundraising", "Mortgage repayments", "Retail operations"]} twoCol />
      <P>This diversified funding model allows Habitat to continue building homes while expanding its services.</P>

      <H3 icon={Heart}>Donations Accepted</H3>
      <P>Habitat welcomes many forms of support.</P>

      <div className="font-display text-lg mt-6">Financial Contributions</div>
      <P>Cash donations remain one of the organization's most important funding sources. Supporters may contribute through:</P>
      <UL items={["One-time gifts", "Monthly donations", "Planned giving", "Memorial gifts", "Corporate matching programs", "Workplace giving", "Fundraising events"]} twoCol />

      <div className="font-display text-lg mt-8">Building Materials</div>
      <P>Many Habitat affiliates accept donations such as:</P>
      <UL items={["Lumber", "Windows", "Doors", "Roofing materials", "Plumbing supplies", "Electrical supplies", "Flooring", "Cabinets", "Paint", "Hardware", "Landscaping materials"]} twoCol />
      <P>These donations help reduce construction costs.</P>

      <div className="font-display text-lg mt-8">Household Goods</div>
      <P>Many local Habitat organizations operate <b>Habitat ReStores</b>, which sell donated:</P>
      <UL items={["Furniture", "Appliances", "Cabinets", "Lighting", "Building supplies", "Home décor", "Flooring", "Tools"]} twoCol />
      <P>
        Revenue from ReStores helps fund additional housing projects while keeping reusable materials out of landfills.<Cite />
      </P>

      <H3 icon={Users}>Volunteers</H3>
      <P>One of Habitat's greatest strengths is its volunteer network. Volunteers include:</P>
      <UL items={["Construction professionals", "Students", "Church groups", "Community organizations", "Corporate teams", "Retirees", "Families", "International volunteers"]} twoCol />
      <P>No construction experience is required for many volunteer opportunities, making Habitat accessible to people from all walks of life.</P>

      <H3 icon={Shield}>Disaster Recovery</H3>
      <P>Habitat also assists communities recovering from:</P>
      <UL items={["Hurricanes", "Floods", "Tornadoes", "Earthquakes", "Wildfires"]} twoCol />
      <P>Rather than focusing solely on emergency shelter, Habitat often helps families rebuild stronger, safer homes for long-term recovery.</P>

      <H3 icon={Globe}>International Operations</H3>
      <P>
        What began in Georgia has become a global movement. Habitat now works across all 50 U.S. states and in approximately 70 countries through locally managed organizations that adapt programs to local housing needs, building methods, and economic conditions.<Cite />
      </P>
      <P>Projects may include:</P>
      <UL items={["New home construction", "Home repairs", "Community development", "Water and sanitation improvements", "Disaster recovery", "Housing advocacy", "Financial inclusion", "Land rights initiatives"]} twoCol />

      <H3 icon={TrendingUp}>Impact</H3>
      <P>
        Since its founding, Habitat has helped more than <b>65 million people</b> build or improve a place they can call home. Its impact extends beyond housing to improvements in health, education, economic opportunity, and community resilience.<Cite />
      </P>

      <H3>Jimmy and Rosalynn Carter</H3>
      <P>One of Habitat's most recognizable supporters was former U.S. President <b>Jimmy Carter</b>, together with former First Lady <b>Rosalynn Carter</b>.</P>
      <P>
        Beginning in 1984, they participated in annual building projects that attracted thousands of volunteers and significantly increased Habitat's visibility around the world. Their involvement helped establish Habitat as one of the best-known charitable housing organizations globally.<Cite />
      </P>

      <H2>Why Habitat Is Widely Respected</H2>
      <P>Habitat enjoys a strong international reputation because it:</P>
      <UL items={["Empowers rather than simply assists families.", "Maintains a large volunteer base.", "Encourages community participation.", "Focuses on long-term affordability.", "Promotes financial responsibility.", "Partners with local organizations.", "Emphasizes transparency and accountability."]} twoCol />
      <P>Its model has inspired countless housing nonprofits worldwide.</P>

      <H2>Criticisms and Challenges</H2>
      <P>Like any large international nonprofit, Habitat has faced criticism over the years. Common concerns raised by observers and some participants include:</P>
      <UL items={["Long waiting lists due to limited resources.", "Strict eligibility requirements that may exclude some families.", "Local affiliate rules that vary considerably from one community to another.", "Questions about homeowner restrictions in certain affiliates after purchase.", "Ongoing challenges in meeting the growing demand for affordable housing."]} />
      <P>
        Habitat has also adapted its model over time, expanding beyond new-home construction to include home repairs, neighborhood revitalization, advocacy, and housing policy initiatives in response to changing community needs.<Cite />
      </P>

      <H2>Could Other Countries Create Similar Organizations?</H2>
      <P className="italic">Absolutely.</P>
      <P>
        Habitat's approach demonstrates that successful housing organizations do not necessarily require enormous budgets. What they do require is <b>strong community partnerships and a sustainable operating model</b>.
      </P>
      <P>Organizations inspired by Habitat could:</P>
      <UL items={["Partner with local governments.", "Recruit volunteers.", "Engage local builders.", "Source donated materials.", "Develop affordable financing.", "Encourage homeowner participation.", "Promote community ownership.", "Build revolving housing funds to finance future projects."]} twoCol />
      <P>By adapting these principles to local laws, cultures, and economic conditions, communities around the world can create sustainable housing programs tailored to their own needs.</P>

      <H2>Looking to the Future</H2>
      <P>
        Housing affordability remains one of the world's greatest social challenges. Rapid urbanization, population growth, rising construction costs, and climate-related disasters continue to increase demand for safe and affordable housing.
      </P>
      <P>
        Habitat for Humanity is responding by expanding its work beyond building homes to include housing advocacy, climate resilience, neighborhood revitalization, and partnerships that improve access to housing finance and infrastructure. As housing challenges evolve, the organization continues to adapt while remaining committed to its founding belief that <b>decent housing should be within everyone's reach</b>.
      </P>

      <div className="mt-14 wofy-card border-l-4" style={{ borderColor: "var(--wofy-primary)" }}>
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>LESSONS FOR SOCIAL PULSE</div>
        <p className="text-base leading-relaxed">
          Habitat for Humanity demonstrates that lasting solutions are built through <b>partnership rather than dependency</b>. Families contribute, volunteers serve, donors invest, and communities work together toward a shared goal.
        </p>
        <p className="mt-4 text-base leading-relaxed">
          Social Pulse embraces these same principles. By combining compassion with practical action, organizations can help people not only receive assistance but also build stronger, healthier, and more self-reliant lives. Whether through housing, food security, education, healthcare, or community development, meaningful change happens when people work together with dignity, respect, and hope.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/donate" className="wofy-btn-primary" data-testid="habitat-donate-cta">
            <Heart className="w-4 h-4" /> Support Social Pulse
          </Link>
          <Link to="/housing" className="wofy-btn-ghost">
            <Home className="w-4 h-4" /> View housing projects
          </Link>
        </div>
      </div>
    </article>
  );
}
